import * as React from 'react'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Resident } from '@/features/residents/api'
import { createVital, type CreateVitalInput } from './api'
import { Button } from '@/components/primitives/Button'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import './VitalsForm.css'

interface Props {
  resident: Resident
  lastTakenAt?: string | undefined
}

type RegularKey = keyof Omit<CreateVitalInput, 'resident_id' | 'taken_at' | 'notes' | 'bp_systolic' | 'bp_diastolic'>

type FieldDef =
  | { type: 'bp' }
  | {
      type: 'regular'
      key: RegularKey
      label: string
      unit: string
      placeholder?: string
      step?: number
      min?: number
      max?: number
    }

// Physiologically-plausible *data-entry* bounds for elderly LTC residents —
// NOT clinical "normal" ranges. Wide enough to admit any real reading
// (hypothermia, severe brady/tachycardia, cachexia → bariatric, DKA), tight
// enough to reject typos and impossible entries. These MUST stay in sync with
// the authoritative server guard in server/app/routers/vitals.py (VITAL_RANGES).
//
// `step` mirrors the field's true precision so the browser's own number hints
// agree with our JS validation: integer fields step by 1, decimal fields by the
// smallest meaningful increment (temp 0.1, spirometry 0.01). `min` reflects the
// real floor — for measured vitals 0 means "no patient / probe off", never a
// recordable reading, so the floor is always above 0 (pain score, the lone
// exception, isn't on this form).
const FIELDS: FieldDef[] = [
  { type: 'bp' },
  { type: 'regular', key: 'blood_glucose',          label: 'Blood Glucose',          unit: 'mg/dL',  placeholder: '110',  min: 10,   max: 1000 },
  { type: 'regular', key: 'temperature_f',           label: 'Body Temperature',       unit: '°F',     placeholder: '98.6', step: 0.1,  min: 77,   max: 113 },
  { type: 'regular', key: 'heart_rate',              label: 'Heart Rate',             unit: 'bpm',    placeholder: '72',   min: 20,   max: 300 },
  { type: 'regular', key: 'respiratory_rate',        label: 'Respiration Rate',       unit: '/min',   placeholder: '16',   min: 4,    max: 60 },
  { type: 'regular', key: 'height_in',               label: 'Height',                 unit: 'in',     placeholder: '65',   step: 0.1,  min: 24,   max: 90 },
  { type: 'regular', key: 'weight_lb',               label: 'Weight',                 unit: 'lb',     placeholder: '165',  step: 0.1,  min: 40,   max: 600 },
  { type: 'regular', key: 'spo2',                    label: 'Oxygen Saturation',      unit: 'SpO₂ %', placeholder: '97',   min: 50,   max: 100 },
  { type: 'regular', key: 'pulse_rate',              label: 'Pulse Rate',             unit: 'bpm',    placeholder: '72',   min: 20,   max: 300 },
  { type: 'regular', key: 'bmi',                     label: 'Body Mass Index',        unit: 'kg/m²',  placeholder: '24.5', step: 0.1,  min: 10,   max: 80 },
  { type: 'regular', key: 'respiratory_volume',      label: 'Respiratory Volume',     unit: 'L',      placeholder: '0.5',  step: 0.01, min: 0.1,  max: 3 },
  { type: 'regular', key: 'perfusion_index',         label: 'Perfusion Index',        unit: '%',      placeholder: '3.0',  step: 0.01, min: 0.02, max: 20 },
  { type: 'regular', key: 'peak_expiratory_flow',    label: 'Peak Expiratory Flow',   unit: 'L/min',  placeholder: '400',  min: 50,   max: 800 },
  { type: 'regular', key: 'force_expiratory_volume', label: 'Forced Expiratory Volume', unit: 'L',    placeholder: '3.5',  step: 0.01, min: 0.3,  max: 6 },
]

type FormState = Record<string, string>
type ErrorState = Record<string, string>

const BP_SYS = { min: 50, max: 300, label: 'Systolic' }
const BP_DIA = { min: 20, max: 200, label: 'Diastolic' }

// Min/max + label + step for a field key, so validation/sanitizing can run live
// (on change), not only on submit. BP keys aren't in FIELDS — BP is integer-only
// (step 1) — so they're resolved here too.
function rangeFor(key: string): { min?: number; max?: number; label: string; step: number } {
  if (key === 'bp_systolic') return { min: BP_SYS.min, max: BP_SYS.max, label: BP_SYS.label, step: 1 }
  if (key === 'bp_diastolic') return { min: BP_DIA.min, max: BP_DIA.max, label: BP_DIA.label, step: 1 }
  const f = FIELDS.find((x) => x.type === 'regular' && x.key === key)
  if (f && f.type === 'regular') return { min: f.min, max: f.max, label: f.label, step: f.step ?? 1 }
  return { label: 'Value', step: 1 }
}

// How many decimal places a field allows, derived from its step. Integer fields
// (step 1) allow none; step 0.1 → 1 place; step 0.01 → 2 places.
function decimalsForStep(step: number): number {
  if (!Number.isFinite(step) || step >= 1) return 0
  const s = String(step)
  const dot = s.indexOf('.')
  return dot === -1 ? 0 : s.length - dot - 1
}

function validateField(raw: string, min?: number, max?: number, label?: string): string | null {
  if (raw === '' || raw === undefined) return null
  // Partial entries while typing ("-", ".", "-.") aren't numbers yet — wait for
  // a digit before judging, so no error flashes between the sign and the value.
  if (raw === '-' || raw === '.' || raw === '-.') return null
  const n = Number(raw)
  if (Number.isNaN(n)) return `${label ?? 'Value'} must be a number`
  if (min !== undefined && n < min) return `Min is ${min}`
  if (max !== undefined && n > max) return `Max is ${max}`
  return null
}

// Strip anything that isn't a digit, a single leading minus, or a decimal point
// as the user types — so letters, symbols, and pasted junk can't enter the
// field. A leading "-" is KEPT (negatives are nonsensical for a vital, but we
// let them be typed so the nurse sees a clear "Min is …" error rather than
// having the sign silently vanish — same treatment as 0). `maxDecimals` caps
// the fractional part to the field's precision (from its step): integer fields
// drop the decimal point entirely, step 0.1 keeps one place, step 0.01 keeps
// two. This stops runaway entries like "0.000000000000" or pasted junk.
function sanitizeNumeric(raw: string, maxDecimals: number): string {
  // Preserve only a single leading minus, then drop every other "-".
  const neg = raw.trimStart().startsWith('-')
  let s = raw.replace(/[^0-9.]/g, '')
  const dot = s.indexOf('.')
  if (dot !== -1) {
    // Collapse to a single decimal point, then cap fractional digits.
    const intPart = s.slice(0, dot)
    let frac = s.slice(dot + 1).replace(/\./g, '')
    if (maxDecimals <= 0) s = stripLeadingZeros(intPart)
    else {
      frac = frac.slice(0, maxDecimals)
      s = stripLeadingZeros(`${intPart}.${frac}`)
    }
  } else {
    s = stripLeadingZeros(s)
  }
  // Re-attach the sign. A lone "-" stays so the field can show it mid-type.
  return neg ? '-' + s : s
}

// Normalize leading zeros: "000000000" collapses and "007" becomes "7", while a
// lone "0" is kept so it stays in the box and surfaces its "Min is …" error
// (0 is below every vital's floor — the nurse should see why, not have it
// silently vanish). A leading decimal gets its "0" restored (".5" → "0.5",
// ".00" → "0.00") so the field never displays a bare-dot value.
function stripLeadingZeros(s: string): string {
  if (s.startsWith('.')) return '0' + s
  return s.replace(/^0+(?=\d)/, '')
}

function formatLastCollected(iso: string): string {
  const safeIso = /[Z+]/.test(iso.slice(10)) ? iso : iso + 'Z'
  const d = new Date(safeIso)
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  return d.toLocaleString('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export function VitalsForm({ resident, lastTakenAt }: Props): React.JSX.Element {
  const qc = useQueryClient()
  const toast = useToast()
  const [values, setValues] = useState<FormState>({})
  const [errors, setErrors] = useState<ErrorState>({})
  const [notes, setNotes] = useState('')

  // Clear any unsaved draft when switching to a different resident.
  React.useEffect(() => {
    setValues({})
    setErrors({})
    setNotes('')
  }, [resident.id])

  const hasAnyValue =
    Object.values(values).some((v) => v !== '') || notes.trim() !== ''

  const mutation = useMutation({
    mutationFn: (input: CreateVitalInput) => createVital(input),
    onSuccess: () => {
      toast.success('Reading saved', 'Vitals recorded successfully.')
      setValues({})
      setNotes('')
      void qc.invalidateQueries({ queryKey: ['vitals', resident.id] })
      // A new vital bumps the resident's updated_at server-side; refresh the
      // residents list so they re-sort to the top.
      void qc.invalidateQueries({ queryKey: ['residents'] })
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        toast.error('Save failed', err.message)
      } else {
        toast.error('Save failed', 'Try again in a moment.')
      }
    },
  })

  function setField(key: string, raw: string): void {
    const { min, max, label, step } = rangeFor(key)
    // Strip non-numeric junk and cap the value to the field's decimal precision
    // on input — never rewrite the magnitude. Both the floor and the ceiling
    // surface as live inline errors (e.g. "Max is 300") so what the nurse typed
    // is always what they see; an out-of-range value is flagged, not silently
    // snapped, and is blocked again on submit.
    const clean = sanitizeNumeric(raw, decimalsForStep(step))
    setValues((v) => ({ ...v, [key]: clean }))
    const err = validateField(clean, min, max, label)
    setErrors((e) => {
      const next = { ...e }
      if (err) next[key] = err
      else delete next[key]
      return next
    })
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault()

    // Validate all non-empty fields and collect errors
    const nextErrors: ErrorState = {}
    const sysRaw = values['bp_systolic']
    const diaRaw = values['bp_diastolic']
    const sysErr = validateField(sysRaw ?? '', BP_SYS.min, BP_SYS.max, BP_SYS.label)
    const diaErr = validateField(diaRaw ?? '', BP_DIA.min, BP_DIA.max, BP_DIA.label)
    if (sysErr) nextErrors['bp_systolic'] = sysErr
    if (diaErr) nextErrors['bp_diastolic'] = diaErr
    // Systolic must exceed diastolic — only check once both are valid numbers.
    if (sysRaw && diaRaw && !sysErr && !diaErr && Number(sysRaw) <= Number(diaRaw)) {
      nextErrors['bp_systolic'] = 'Systolic must be greater than diastolic'
    }
    for (const f of FIELDS) {
      if (f.type !== 'regular') continue
      const err = validateField(values[f.key] ?? '', f.min, f.max, f.label)
      if (err) nextErrors[f.key] = err
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      toast.error('Invalid values', 'Fix the highlighted fields before saving.')
      return
    }

    const payload: CreateVitalInput = { resident_id: resident.id }
    const sink = payload as unknown as Record<string, number>

    // BP pair
    if (sysRaw) payload.bp_systolic = Number(sysRaw)
    if (diaRaw) payload.bp_diastolic = Number(diaRaw)

    // All regular fields
    for (const f of FIELDS) {
      if (f.type !== 'regular') continue
      const raw = values[f.key]
      if (raw === undefined || raw === '') continue
      sink[f.key] = Number(raw)
    }

    const hasAny = Object.keys(payload).length > 1
    if (!hasAny) {
      toast.warning('Nothing to save', 'Enter at least one reading first.')
      return
    }

    if (notes.trim()) payload.notes = notes.trim()

    mutation.mutate(payload)
  }

  function onClear(): void {
    setValues({})
    setErrors({})
    setNotes('')
  }

  return (
    <form className="vitals-form" onSubmit={onSubmit} noValidate>
      <header className="vitals-form-head">
        <div className="vitals-section-label">
          <PlusIcon />
          <span>COLLECT VITALS</span>
        </div>
      </header>

      <div className="vitals-grid">
        {FIELDS.map((f) =>
          f.type === 'bp' ? (
            <BPCell
              key="bp"
              sys={values['bp_systolic'] ?? ''}
              dia={values['bp_diastolic'] ?? ''}
              onSys={(v) => setField('bp_systolic', v)}
              onDia={(v) => setField('bp_diastolic', v)}
              sysError={errors['bp_systolic']}
              diaError={errors['bp_diastolic']}
            />
          ) : (
            <FieldCell
              key={String(f.key)}
              def={f}
              value={values[f.key] ?? ''}
              onChange={(raw) => setField(f.key, raw)}
              error={errors[f.key]}
            />
          )
        )}
      </div>

      <label className="vitals-notes">
        <span className="vitals-notes-label">Notes</span>
        <textarea
          className="vitals-notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Anything the team should know about this reading…"
        />
      </label>

      <footer className="vitals-form-foot">
        <Button variant="ghost" type="button" onClick={onClear} disabled={mutation.isPending}>
          Clear
        </Button>
        <Button type="submit" loading={mutation.isPending} disabled={!hasAnyValue} leftIcon={<CheckIcon />}>
          Save reading
        </Button>
        {lastTakenAt ? (
          <span className="vitals-last-info muted small">
            Last collected{' '}
            <time dateTime={lastTakenAt} className="mono">
              {formatLastCollected(lastTakenAt)}
            </time>
          </span>
        ) : null}
      </footer>
    </form>
  )
}

function BPCell({
  sys,
  dia,
  onSys,
  onDia,
  sysError,
  diaError,
}: {
  sys: string
  dia: string
  onSys: (v: string) => void
  onDia: (v: string) => void
  sysError?: string
  diaError?: string
}): React.JSX.Element {
  const hasError = sysError || diaError
  return (
    <div className="vitals-field">
      <span className="vitals-field-label">
        Blood Pressure <span className="vitals-field-unit">· mmHg</span>
      </span>
      <div className="vitals-bp-pair">
        <input
          type="text"
          inputMode="decimal"
          min={BP_SYS.min}
          max={BP_SYS.max}
          value={sys}
          onChange={(e) => onSys(e.target.value)}
          placeholder="128"
          className={`vitals-field-input mono${sysError ? ' vitals-field-input--error' : ''}`}
          aria-label="Systolic"
          aria-invalid={!!sysError}
        />
        <span className="vitals-bp-sep">/</span>
        <input
          type="text"
          inputMode="decimal"
          min={BP_DIA.min}
          max={BP_DIA.max}
          value={dia}
          onChange={(e) => onDia(e.target.value)}
          placeholder="82"
          className={`vitals-field-input mono${diaError ? ' vitals-field-input--error' : ''}`}
          aria-label="Diastolic"
          aria-invalid={!!diaError}
        />
      </div>
      {hasError && (
        <span className="vitals-field-error">{sysError ?? diaError}</span>
      )}
    </div>
  )
}

function FieldCell({
  def,
  value,
  onChange,
  error,
}: {
  def: Extract<FieldDef, { type: 'regular' }>
  value: string
  onChange: (raw: string) => void
  error?: string
}): React.JSX.Element {
  return (
    <label className="vitals-field">
      <span className="vitals-field-label">
        {def.label} <span className="vitals-field-unit">· {def.unit}</span>
      </span>
      <input
        type="text"
        inputMode="decimal"
        min={def.min}
        max={def.max}
        step={def.step ?? 1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={def.placeholder}
        className={`vitals-field-input mono${error ? ' vitals-field-input--error' : ''}`}
        aria-invalid={!!error}
      />
      {error && <span className="vitals-field-error">{error}</span>}
    </label>
  )
}

function PlusIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  )
}

function CheckIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}
